import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS = {
  pendente: { l: "Pendente", c: "bg-[#FFCC00]" },
  aceito: { l: "Aceito", c: "bg-[#00A859] text-white" },
  recusado: { l: "Recusado", c: "bg-[#E60000] text-white" },
};

export default function Aditivos() {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [montadores, setMontadores] = useState([]);
  const [lista, setLista] = useState([]);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState({ montador_id: "", titulo: "", conteudo: "", tipo: "financeiro", vigencia_inicio: "" });
  const [det, setDet] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
    api.get("/montadores").then(r => setMontadores(r.data));
  }, [user]);

  const load = async () => {
    if (!empresaId) return;
    const r = await api.get(`/empresas/${empresaId}/aditivos`);
    setLista(r.data);
  };

  useEffect(() => { load(); }, [empresaId]);

  const salvar = async () => {
    if (!form.montador_id || !form.titulo || !form.conteudo) { toast.error("Preencha todos os campos"); return; }
    setSalvando(true);
    try {
      const r = await api.post(`/empresas/${empresaId}/aditivos`, form);
      toast.success(r.data.ia_disponivel ? "Aditivo criado · IA revisou" : "Aditivo criado");
      setNovo(false); setForm({ montador_id: "", titulo: "", conteudo: "", tipo: "financeiro", vigencia_inicio: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap">Compliance · Empresa</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Aditivos Contratuais</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Envio de aditivos com aceite eletrônico (IP/dispositivo/versão registrados no log jurídico imutável).
          </p>
        </div>
        <Button onClick={() => setNovo(true)} disabled={!empresaId} className="btn-sharp brand-bg text-white"
          data-testid="ad-novo">+ Novo aditivo</Button>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="max-w-md" data-testid="ad-empresa"><SelectValue placeholder="Selecione empresa" /></SelectTrigger>
            <SelectContent>{empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F4F5] border-b border-border">
            <tr>
              <th className="text-left p-3 label-cap">Versão</th>
              <th className="text-left p-3 label-cap">Montador</th>
              <th className="text-left p-3 label-cap">Título</th>
              <th className="text-left p-3 label-cap">Tipo</th>
              <th className="text-left p-3 label-cap">Status</th>
              <th className="text-left p-3 label-cap">Vigência</th>
              <th className="text-left p-3 label-cap">Aceite em</th>
              <th className="text-left p-3 label-cap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={8} className="p-12 text-center text-muted-foreground">Nenhum aditivo</td></tr>}
            {lista.map(a => {
              const st = STATUS[a.status] || STATUS.pendente;
              return (
                <tr key={a.id} className="border-b border-border last:border-0" data-testid={`ad-${a.id}`}>
                  <td className="p-3 font-mono text-xs">#{a.versao}</td>
                  <td className="p-3 font-medium">{a.montador_nome}</td>
                  <td className="p-3">{a.titulo}</td>
                  <td className="p-3 text-xs uppercase">{a.tipo}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-[10px] uppercase ${st.c}`}>{st.l}</span></td>
                  <td className="p-3 text-xs">{a.vigencia_inicio || "—"}</td>
                  <td className="p-3 text-xs">{a.aceito_em ? new Date(a.aceito_em).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="p-3">
                    <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]"
                      onClick={() => setDet(a)} data-testid={`ad-ver-${a.id}`}>Ver</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={novo} onOpenChange={setNovo}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Novo aditivo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-cap">Montador *</Label>
              <Select value={form.montador_id} onValueChange={(v) => setForm({ ...form, montador_id: v })}>
                <SelectTrigger data-testid="ad-montador"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{montadores.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-cap">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="ad-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="financeiro">Financeiro</SelectItem>
                    <SelectItem value="comercial">Comercial</SelectItem>
                    <SelectItem value="operacional">Operacional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-cap">Vigência</Label>
                <Input type="date" value={form.vigencia_inicio}
                  onChange={(e) => setForm({ ...form, vigencia_inicio: e.target.value })}
                  data-testid="ad-vigencia" />
              </div>
            </div>
            <div>
              <Label className="label-cap">Título *</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="ad-titulo" />
            </div>
            <div>
              <Label className="label-cap">Conteúdo * (IA revisa)</Label>
              <Textarea rows={6} value={form.conteudo}
                onChange={(e) => setForm({ ...form, conteudo: e.target.value })} data-testid="ad-conteudo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovo(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={salvando} className="brand-bg text-white" data-testid="ad-salvar">
              {salvando ? "IA revisando…" : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!det} onOpenChange={(o) => !o && setDet(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{det?.titulo}</DialogTitle></DialogHeader>
          {det && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-muted-foreground">
                Versão #{det.versao} · {det.montador_nome} · {det.tipo}
              </div>
              <div className="border-l-4 border-[#00A859] p-3 bg-green-50 whitespace-pre-wrap">{det.conteudo}</div>
              {det.aceito_em && (
                <div className="border-l-4 border-[#142766] p-3 bg-[#142766]/5 text-xs">
                  ✓ Aceito em {new Date(det.aceito_em).toLocaleString("pt-BR")}<br/>
                  IP: {det.ip_aceite} · {det.dispositivo_aceite?.slice(0, 80)}…
                </div>
              )}
              {det.recusado_em && (
                <div className="border-l-4 border-[#E60000] p-3 bg-red-50 text-xs">
                  ✗ Recusado em {new Date(det.recusado_em).toLocaleString("pt-BR")}<br/>
                  Motivo: {det.motivo_recusa}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
